import { getPropertyAddress, NULL_TIME, type Transaction } from '@kalos/kalos-rpc';
import { Button as UiButton, Dialog, DialogContent } from '@kalos/ui';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { default as Button } from '@mui/material/Button';
import { default as Collapse } from '@mui/material/Collapse/Collapse';
import { BackpackIcon, CameraIcon } from '@radix-ui/react-icons';
import { type ComponentProps, type FC, Fragment, useCallback } from 'react';

import { IRS_SUGGESTED_MILE_FACTOR } from '../../../constants';
import { useAuth } from '../../../context/AuthContext';
import {
  formatDate,
  roundNumber,
  TimesheetDepartmentClientService,
  usd,
} from '../../../tools/helpers';
import { TransactionFilesGallery } from '../../AltGallery/main';
import { Loader } from '../../Loader/main';
import { Button as KalosButton } from '../Button';
import { InfoTable } from '../InfoTable';
import { PrintList } from '../PrintList';
import { PrintPage } from '../PrintPage';
import { PrintParagraph } from '../PrintParagraph';
import { PrintTable } from '../PrintTable';
import { ReconcileAccountsPayableDialog } from '../ReconcileAccountsPayable';
import { SectionBar } from '../SectionBar';
import { Tabs } from '../Tabs';
import { Tooltip } from '../Tooltip';
import { EmployeeSummaryView } from './EmployeeSummaryView';
import { ACTIONS } from './reducer';
import {
  getPerDiemStatus,
  getPerDiemTotalsFromRows,
  getReimbursementStatus,
  getSpiffStatus,
  getTimesheetStatus,
  getTransactionStatus,
  getTripStatus,
  isDuplicateTransaction,
  LODGING_TRAVEL_COST_CENTER_ID,
  reimbursementCounts,
  spiffCounts,
  tabs,
  transformTimesheetsToDataList,
  useCostReport,
} from './useCostReport';

export interface Props {
  serviceCallId: number;
  onClose?: () => void;
  title?: ComponentProps<typeof SectionBar>['title'];
  loader?: React.ReactNode;
}

export type SearchType = {
  technicians: string;
  statusId: number;
  priorityId: number;
};
export type { Week } from './useCostReport';
export const GetTotalTransactions = (transactions: Transaction[]) => {
  return transactions.reduce((aggr, txn) => aggr + txn.amount, 0);
};

export const CostReportCSV: FC<Props> = ({ serviceCallId, onClose, title, loader }) => {
  const loggedUser = useAuth().user;
  const hasReconcilePermission = loggedUser.permissionGroups.some(
    (pg) => pg.name === 'AccountsPayableReconcile',
  );
  const {
    state,
    dispatch,
    sortTransactions,
    downloadPageCSV,
    downloadFullReportCSV,
    downloadReceiptsZipMutation,
    downloadReceiptsZip,
    costCenterArray,
    laborCostArray,
    employeeHourTotals,
    totalMeals,
    totalTransactions,
    totalLodging,
    totalTasksBillable,
    totalReimbursements,
    totalMiles,
    dateSortColumn,
    sortedPerDiems,
    sortedTimesheets,
    sortedTrips,
    sortedTasks,
    sortedReimbursements,
    weekArray,
  } = useCostReport(serviceCallId);

  const handlePrint = useCallback(async () => {
    dispatch({ type: ACTIONS.SET_PRINT_STATUS, data: 'loading' });
    dispatch({ type: ACTIONS.SET_PRINT_STATUS, data: 'loaded' });
  }, [dispatch]);
  const handlePrinted = useCallback(
    () => dispatch({ type: ACTIONS.SET_PRINT_STATUS, data: 'idle' }),
    [dispatch],
  );
  return state.loaded ? (
    <SectionBar key="ReportPage" uncollapsable={true} title={title}>
      <style>{`
  .InfoTableItem {
    font-size: 10px;
    font-size: 1.0vw;

    }
  `}</style>
      {state.openReconcileId !== undefined && (
        <ReconcileAccountsPayableDialog
          transactionId={state.openReconcileId}
          defaultJobId={serviceCallId}
          open={state.openReconcileId !== undefined}
          onOpenChange={(open) => {
            if (!open) {
              dispatch({ type: ACTIONS.SET_OPEN_RECONCILE_ID, data: undefined });
            }
          }}
          onReconciled={() => {
            dispatch({ type: ACTIONS.SET_OPEN_RECONCILE_ID, data: undefined });
            dispatch({ type: ACTIONS.SET_LOADED, data: false });
          }}
        />
      )}
      <div className="print-actions">
        <PrintPage
          buttonProps={{
            label: 'Print Cost Report',
            loading: state.loading,
          }}
          downloadLabel="Download Cost Report"
          downloadPdfFilename="Cost-Report"
          headerProps={{ title: 'Project Cost Report' }}
          onPrint={handlePrint}
          onPrinted={handlePrinted}
          status={state.printStatus}
          key={state.printStatus}
          className="EditProjectAsideContent"
        >
          <PrintParagraph tag="h2" key="eventPDFHeader">
            Project Details
          </PrintParagraph>
          {state.event && (
            <PrintList
              key="eventColumns"
              items={[
                <Fragment key="address">
                  <strong>Address: </strong>
                  {getPropertyAddress(state.event.property)}
                </Fragment>,
                <Fragment key="startDate">
                  <strong>Start Date: </strong>
                  {formatDate(state.event.dateStarted)}
                </Fragment>,
                <Fragment key="endDate">
                  <strong>End Date: </strong>
                  {formatDate(state.event.dateEnded)}
                </Fragment>,
                <Fragment key="jobNumber">
                  <strong>Job Number: </strong>
                  {state.event.logJobNumber}
                </Fragment>,
              ]}
            />
          )}
          <PrintParagraph tag="h2" key="totalHeader1">
            Summary Info
          </PrintParagraph>
          <PrintTable
            key="totalColumns1"
            columns={[
              { title: 'Type', align: 'left' },
              { title: 'Total', align: 'right' },
            ]}
            data={[
              [
                'Total Hours Worked',
                state.totalHoursWorked > 1
                  ? `${state.totalHoursWorked} hrs`
                  : state.totalHoursWorked == 0
                    ? 'None'
                    : `${state.totalHoursWorked} hr`,
              ],
              [
                <PrintTable
                  key="TransactionBreakdownCategory"
                  columns={[
                    { title: '', align: 'left' },
                    { title: '', align: 'left' },
                  ]}
                  data={laborCostArray.map((labor) => {
                    return labor;
                  })}
                ></PrintTable>,
              ],
            ]}
          />
          {employeeHourTotals.length > 0 && (
            <>
              <PrintParagraph tag="h2" key="employeeHoursHeader">
                Hours by Employee
              </PrintParagraph>
              <PrintTable
                key="employeeHourTotals"
                columns={[
                  { title: 'Employee', align: 'left' },
                  { title: 'Total Hours', align: 'right' },
                ]}
                data={employeeHourTotals.map((employee) => [
                  employee.employeeName,
                  `${roundNumber(employee.hours)} hour(s)`,
                ])}
              />
            </>
          )}
          <PrintParagraph tag="h2" key="totalHeader2">
            Costs
          </PrintParagraph>
          <PrintTable
            key="totalColumns2"
            columns={[
              { title: 'Type', align: 'left' },
              { title: 'Cost', align: 'right' },
            ]}
            data={[
              ['Transactions', usd(totalTransactions)],
              [
                <PrintTable
                  key="TransactionBreakdownCategory"
                  columns={[
                    { title: 'Type', align: 'left' },
                    { title: 'SubCost', align: 'right' },
                  ]}
                  data={costCenterArray.map((costCenter) => {
                    return costCenter;
                  })}
                ></PrintTable>,
              ],
              ['Meals', usd(totalMeals)],
              ['Lodging Processed', usd(totalLodging)],
              ['Spiffs', usd(totalTasksBillable)],
              ['Reimbursements', usd(totalReimbursements)],
              ['Trips Total', usd(state.tripsTotal)],
              ['Total Miles', `${roundNumber(totalMiles)} mi`],
              [
                '',
                <strong key="stronk">
                  TOTAL:{' '}
                  {usd(
                    totalMeals +
                      totalLodging +
                      totalTransactions +
                      totalTasksBillable +
                      totalReimbursements +
                      state.tripsTotal,
                  )}
                </strong>,
              ],
            ]}
          />
          <PrintParagraph tag="h2" key="transactionColumns">
            Transactions
          </PrintParagraph>
          {state.transactionAccounts
            .filter((account) => state.transactions.some((t) => t.costCenterId === account.id))
            .map((account) => (
              <div key={account.id}>
                <PrintParagraph
                  tag="h3"
                  key={`${account.id}-${account.description}`}
                >{`${account.id}-${account.description}`}</PrintParagraph>
                <PrintTable
                  key={`${account.id}-${account.description}${account.accountCategory}`}
                  columns={[
                    {
                      title: 'Department',
                      align: 'left',
                    },
                    {
                      title: 'Owner',
                      align: 'left',
                      widthPercentage: 10,
                    },
                    {
                      title: 'Cost Center / Vendor',
                      align: 'left',
                      widthPercentage: 10,
                    },
                    {
                      title: 'Date',
                      align: 'left',
                      widthPercentage: 10,
                    },
                    {
                      title: 'Amount',
                      align: 'left',
                      widthPercentage: 10,
                    },
                    {
                      title: 'Status',
                      align: 'left',
                      widthPercentage: 10,
                    },
                    {
                      title: 'Notes',
                      align: 'right',
                      widthPercentage: 20,
                    },
                  ]}
                  data={state.transactions
                    .filter((transaction) => transaction.costCenterId === account.id)
                    .map((txn) => {
                      return [
                        txn.department ? (
                          <>
                            {txn.department?.classification} - {txn.department?.description}
                          </>
                        ) : (
                          '-'
                        ),
                        txn.ownerName,
                        <Fragment key={txn.id}>
                          {`${txn.costCenter?.description} - `}
                          <br />
                          {txn.vendor}
                        </Fragment>,
                        formatDate(txn.timestamp),
                        usd(txn.amount),
                        getTransactionStatus(txn),
                        txn.notes,
                      ];
                    })}
                />
              </div>
            ))}
          <PrintParagraph tag="h2" key="perDiemColumns">
            Per Diem
          </PrintParagraph>
          {[...state.perDiems]
            .sort((a, b) => (a.dateSubmitted > b.dateSubmitted ? -1 : 1))
            .map((pd) => {
              const rows = pd.rows;
              const { meals: totalMeals, lodging: totalLodging } = getPerDiemTotalsFromRows(rows);
              if (totalMeals == 0 && totalLodging == 0) {
                return null; // Don't show it
              }
              return (
                <div key={pd.id + 'pdf'}>
                  <PrintParagraph tag="h3">
                    Per Diem {pd.dateSubmitted.split(' ')[0] != NULL_TIME.split(' ')[0] ? '-' : ''}{' '}
                    {pd.dateSubmitted.split(' ')[0] != NULL_TIME.split(' ')[0]
                      ? pd.dateSubmitted.split(' ')[0]
                      : ''}{' '}
                    {pd.ownerName ? '-' : ''} {pd.ownerName}
                  </PrintParagraph>
                  <div
                    key={'PerDiemTablePDF' + pd.id}
                    style={{
                      breakInside: 'avoid',
                      display: 'inline-block',
                      width: '100%',
                    }}
                  >
                    <PrintTable
                      columns={[
                        {
                          title: 'Department',
                          align: 'left',
                        },
                        {
                          title: 'Owner',
                          align: 'left',
                          widthPercentage: 10,
                        },
                        {
                          title: 'Submitted At',
                          align: 'left',
                          widthPercentage: 10,
                        },
                        {
                          title: 'Approved By',
                          align: 'left',
                          widthPercentage: 10,
                        },
                        {
                          title: 'Approved At',
                          align: 'left',
                          widthPercentage: 10,
                        },
                        {
                          title: 'Total Meals',
                          align: 'left',
                          widthPercentage: 10,
                        },
                        {
                          title: 'Total Lodging',
                          align: 'left',
                          widthPercentage: 10,
                        },
                        {
                          title: 'Status',
                          align: 'left',
                          widthPercentage: 10,
                        },
                        {
                          title: 'Notes',
                          align: 'right',
                          widthPercentage: 20,
                        },
                      ]}
                      data={[
                        [
                          TimesheetDepartmentClientService.getDepartmentName(pd.department!),
                          pd.ownerName,
                          pd.dateSubmitted != NULL_TIME ? formatDate(pd.dateSubmitted) : '-',
                          pd.approvedByName || '-',
                          pd.dateApproved != NULL_TIME ? formatDate(pd.dateApproved) : '-',
                          usd(totalMeals),
                          totalLodging != 0 ? usd(totalLodging) : '-',
                          getPerDiemStatus(pd),
                          pd.notes,
                        ],
                      ]}
                    />
                  </div>
                  <PrintParagraph tag="h4">Per Diem Days</PrintParagraph>
                  <div
                    key={'PerDiemDaysPDF'}
                    style={{
                      breakInside: 'avoid',
                      display: 'inline-block',
                      width: '100%',
                    }}
                  >
                    <PrintTable
                      columns={[
                        {
                          title: 'Date',
                          align: 'left',
                        },
                        {
                          title: 'Zip Code',
                          align: 'left',
                          widthPercentage: 10,
                        },
                        {
                          title: 'Meals Only',
                          align: 'left',
                          widthPercentage: 10,
                        },
                        {
                          title: 'Meals',
                          align: 'left',
                          widthPercentage: 10,
                        },
                        {
                          title: 'Lodging',
                          align: 'left',
                          widthPercentage: 10,
                        },
                        {
                          title: 'Notes',
                          align: 'right',
                          widthPercentage: 20,
                        },
                      ]}
                      data={rows.map((pdr) => {
                        return [
                          formatDate(pdr.dateString),
                          pdr.zipCode,
                          pdr.mealsOnly ? 'Yes' : 'No',
                          usd(pdr.mealsValue || 0),
                          pdr.mealsOnly === false ? usd(pdr.lodgingValue || 0) : '-',
                          pdr.notes,
                        ];
                      })}
                    />
                  </div>
                </div>
              );
            })}
          <PrintParagraph tag="h2" key="timesheetsHeader">
            Labor Summary
          </PrintParagraph>
          {state.showEmployeeSummary ? (
            <EmployeeSummaryView
              data={transformTimesheetsToDataList(state.timesheets)}
              startDate={new Date()}
            />
          ) : (
            state.classCodes
              .filter((code) => state.laborTotals[code.id] != undefined)
              .map((code) => (
                <div key={code.id}>
                  <PrintParagraph
                    key={`${code.id}-${code.description}`}
                    tag="h3"
                  >{`${code.id}-${code.description}`}</PrintParagraph>

                  <PrintTable
                    columns={[
                      {
                        title: 'Employee',
                        align: 'left',
                      },
                      {
                        title: 'Department',
                        align: 'left',
                        widthPercentage: 10,
                      },
                      {
                        title: 'Approved By',
                        align: 'left',
                        widthPercentage: 10,
                      },
                      {
                        title: 'Time Started',
                        align: 'left',
                        widthPercentage: 10,
                      },
                      {
                        title: 'Time Finished',
                        align: 'left',
                        widthPercentage: 10,
                      },
                      {
                        title: 'Brief Description',
                        align: 'left',
                        widthPercentage: 10,
                      },
                      {
                        title: 'Hours Worked',
                        align: 'left',
                        widthPercentage: 10,
                      },
                      {
                        title: 'Status',
                        align: 'left',
                        widthPercentage: 10,
                      },
                      {
                        title: 'Notes',
                        align: 'right',
                        widthPercentage: 20,
                      },
                    ]}
                    data={state.timesheets
                      .filter((tsl) => tsl.classCodeId === code.id)
                      .map((tsl) => {
                        return [
                          tsl.technicianUserName,
                          tsl.departmentName,
                          tsl.adminApprovalUserName,
                          formatDate(tsl.timeStarted),
                          formatDate(tsl.timeFinished),
                          tsl.briefDescription,
                          `${tsl.hoursWorked} hour(s)`,
                          getTimesheetStatus(tsl),
                          tsl.notes,
                        ];
                      })}
                  />
                </div>
              ))
          )}
          <PrintParagraph tag="h2" key="tripsHeader">
            Related Trips
          </PrintParagraph>
          {state.trips &&
            state.trips.map((trip) => {
              return (
                <div
                  key={trip.id + 'pdf'}
                  style={{
                    breakInside: 'avoid',
                    display: 'inline-block',
                    width: '100%',
                  }}
                >
                  <PrintTable
                    key="tripTable"
                    columns={[
                      {
                        title: 'Date',
                        align: 'left',
                      },
                      {
                        title: 'Origin Address',
                        align: 'left',
                        widthPercentage: 20,
                      },
                      {
                        title: 'Destination Address',
                        align: 'left',
                        widthPercentage: 20,
                      },
                      {
                        title: 'Distance (Miles)',
                        align: 'left',
                        widthPercentage: 10,
                      },
                      {
                        title: 'Notes',
                        align: 'left',
                        widthPercentage: 10,
                      },
                      {
                        title: 'Home Travel',
                        align: 'right',
                        widthPercentage: 10,
                      },
                      {
                        title: `Cost (${usd(IRS_SUGGESTED_MILE_FACTOR)}/mi)`,
                        align: 'right',
                        widthPercentage: 10,
                      },
                      {
                        title: 'Per Diem Row ID',
                        align: 'right',
                        widthPercentage: 10,
                      },
                      {
                        title: 'Status',
                        align: 'right',
                        widthPercentage: 10,
                      },
                    ]}
                    data={[
                      [
                        formatDate(trip.date),
                        trip.originAddress,
                        trip.destinationAddress,
                        trip.distanceInMiles.toFixed(2),
                        trip.notes,
                        trip.homeTravel,
                        `${usd(
                          Number((trip.distanceInMiles * IRS_SUGGESTED_MILE_FACTOR).toFixed(2)),
                        )}`,
                        trip.perDiemRowId,
                        getTripStatus(trip),
                      ],
                    ]}
                  />
                </div>
              );
            })}
          {state.trips && state.trips.length > 0 && (
            <PrintParagraph tag="h3" key="tripsTotalMiles">
              Total Miles: {roundNumber(totalMiles)} mi
            </PrintParagraph>
          )}
          <PrintParagraph tag="h2" key="tasksHeader">
            Spiffs/Bonus/Commission
          </PrintParagraph>
          {state.tasks &&
            state.tasks.map((task) => {
              return (
                <div
                  key={task.id + 'pdf'}
                  style={{
                    breakInside: 'avoid',
                    display: 'inline-block',
                    width: '100%',
                  }}
                >
                  <PrintTable
                    key="tripTable"
                    columns={[
                      {
                        title: 'Type',
                        align: 'left',
                      },
                      {
                        title: 'Date Performed',
                        align: 'left',
                        widthPercentage: 20,
                      },
                      {
                        title: 'Details',
                        align: 'left',
                        widthPercentage: 20,
                      },
                      {
                        title: 'Flat Rate?',
                        align: 'left',
                        widthPercentage: 10,
                      },
                      {
                        title: 'Amount',
                        align: 'left',
                        widthPercentage: 10,
                      },
                      {
                        title: 'Status',
                        align: 'left',
                        widthPercentage: 10,
                      },
                      {
                        title: 'Notes',
                        align: 'right',
                        widthPercentage: 10,
                      },
                    ]}
                    data={[
                      [
                        task.billableType,
                        formatDate(task.datePerformed),
                        task.details,
                        task.flatRate,
                        task.billableType === 'Spiff'
                          ? usd(task.spiffAmount)
                          : usd(task.toolpurchaseCost),
                        getSpiffStatus(task),
                        task.notes,
                      ],
                    ]}
                  />
                </div>
              );
            })}
          <PrintParagraph tag="h2" key="reimbursementsHeader">
            Reimbursements
          </PrintParagraph>
          {state.reimbursements.length > 0 ? (
            <PrintTable
              key="reimbursementsTable"
              columns={[
                { title: 'Date', align: 'left' },
                { title: 'Owner', align: 'left', widthPercentage: 15 },
                { title: 'Description', align: 'left', widthPercentage: 30 },
                { title: 'Reviewed By', align: 'left', widthPercentage: 15 },
                { title: 'Amount', align: 'right', widthPercentage: 10 },
                { title: 'Status', align: 'right', widthPercentage: 10 },
              ]}
              data={state.reimbursements.map((r) => [
                formatDate(r.createdDate),
                r.ownerName,
                r.description,
                r.reviewerName || '-',
                usd(r.amount),
                getReimbursementStatus(r),
              ])}
            />
          ) : (
            <PrintParagraph key="noReimbursements">
              No reimbursements found for this job.
            </PrintParagraph>
          )}
          <PrintParagraph tag="h2" key="invoiceHeader">
            Invoice & Materials
          </PrintParagraph>
          {state.invoice ? (
            <>
              <PrintParagraph tag="h3" key="servicesPerformedHeader">
                Services Performed
              </PrintParagraph>
              <PrintTable
                key="servicesPerformedTable"
                columns={[
                  { title: 'Description', align: 'left' },
                  { title: 'Amount', align: 'right' },
                ]}
                data={[
                  ...(state.invoice.servicesperformedrow1
                    ? [
                        [
                          state.invoice.servicesperformedrow1,
                          usd(parseFloat(state.invoice.totalamountrow1 || '0')),
                        ],
                      ]
                    : []),
                  ...(state.invoice.servicesperformedrow2
                    ? [
                        [
                          state.invoice.servicesperformedrow2,
                          usd(parseFloat(state.invoice.totalamountrow2 || '0')),
                        ],
                      ]
                    : []),
                  ...(state.invoice.servicesperformedrow3
                    ? [
                        [
                          state.invoice.servicesperformedrow3,
                          usd(parseFloat(state.invoice.totalamountrow3 || '0')),
                        ],
                      ]
                    : []),
                  ...(state.invoice.servicesperformedrow4
                    ? [
                        [
                          state.invoice.servicesperformedrow4,
                          usd(parseFloat(state.invoice.totalamountrow4 || '0')),
                        ],
                      ]
                    : []),
                ]}
              />
              {state.materials.length > 0 && (
                <>
                  <PrintParagraph tag="h3" key="materialsHeader">
                    Materials
                  </PrintParagraph>
                  <PrintTable
                    key="materialsTable"
                    columns={[
                      { title: 'Description', align: 'left' },
                      { title: 'Quantity', align: 'center', widthPercentage: 10 },
                      { title: 'Price', align: 'right', widthPercentage: 10 },
                      { title: 'Billable', align: 'center', widthPercentage: 10 },
                      { title: 'Total', align: 'right', widthPercentage: 10 },
                    ]}
                    data={state.materials.map((material) => {
                      const total = material.isBillable
                        ? material.quantity * material.quotedPrice
                        : 0;
                      return [
                        material.description,
                        material.quantity.toString(),
                        usd(material.quotedPrice),
                        material.isBillable ? 'Yes' : 'No',
                        usd(total),
                      ];
                    })}
                  />
                </>
              )}
              <PrintTable
                key="invoiceTotalsTable"
                columns={[
                  { title: '', align: 'left' },
                  { title: '', align: 'right' },
                ]}
                data={[
                  ...(state.invoice.discountcost && parseFloat(state.invoice.discountcost) > 0
                    ? [['Discount', usd(parseFloat(state.invoice.discountcost))]]
                    : []),
                  ...(state.event?.taxCost ? [['Sales Tax', usd(state.event.taxCost)]] : []),
                  [
                    <strong key="grandTotalLabel">Grand Total</strong>,
                    <strong key="grandTotalValue">
                      {usd(
                        parseFloat(state.invoice.totalamountrow1 || '0') +
                          parseFloat(state.invoice.totalamountrow2 || '0') +
                          parseFloat(state.invoice.totalamountrow3 || '0') +
                          parseFloat(state.invoice.totalamountrow4 || '0') +
                          parseFloat(state.invoice.materialTotal || '0') -
                          parseFloat(state.invoice.discountcost || '0') +
                          (state.event?.taxCost || 0),
                      )}
                    </strong>,
                  ],
                ]}
              />
            </>
          ) : (
            <PrintParagraph key="noInvoice">No invoice found for this job.</PrintParagraph>
          )}
          <PrintParagraph tag="h2" key="paymentsHeader">
            Payments
          </PrintParagraph>
          {state.payments.length > 0 ? (
            <PrintTable
              key="paymentsTable"
              columns={[
                { title: 'Service Rendered ID', align: 'left', widthPercentage: 20 },
                { title: 'Amount Collected', align: 'right', widthPercentage: 20 },
                { title: 'Type', align: 'left', widthPercentage: 20 },
                { title: 'Collected', align: 'left', widthPercentage: 20 },
              ]}
              data={[
                ...state.payments.map((payment) => [
                  payment.servicesRenderedId?.toString() || '-',
                  usd(payment.amountCollected || 0),
                  payment.type || '-',
                  payment.collected ? 'Yes' : 'No',
                ]),
                [
                  <strong key="totalLabel">Total Payments</strong>,
                  <strong key="totalValue">
                    {usd(
                      state.payments.reduce(
                        (aggr, payment) => aggr + (payment.amountCollected || 0),
                        0,
                      ),
                    )}
                  </strong>,
                  '',
                  '',
                ],
              ]}
            />
          ) : (
            <PrintParagraph key="noPayments">No payments found for this job.</PrintParagraph>
          )}
        </PrintPage>
        <KalosButton
          style={{ display: 'inline-block' }}
          label="Download Cost Report (CSV)"
          onClick={downloadFullReportCSV}
        ></KalosButton>
        <KalosButton
          style={{ display: 'inline-block' }}
          label="Download CSV of Page"
          onClick={downloadPageCSV}
        ></KalosButton>
        <KalosButton
          style={{ display: 'inline-block' }}
          label="Download Receipts (Zip)"
          onClick={downloadReceiptsZip}
          disabled={downloadReceiptsZipMutation.isPending || state.transactions.length === 0}
          loading={downloadReceiptsZipMutation.isPending}
        ></KalosButton>
        {onClose && <KalosButton onClick={onClose} label="Close"></KalosButton>}
      </div>
      <Tabs
        onChange={(e) => dispatch({ type: ACTIONS.SET_ACTIVE_TAB, data: tabs[e] })}
        tabs={[
          {
            label: 'Job Details',
            content: state.event && (
              <div>
                <PrintList
                  className="p-4"
                  items={[
                    <div key="address" style={{ fontSize: '1.0vw' }}>
                      <strong>Address: </strong>
                      {getPropertyAddress(state.event.property)}
                    </div>,
                    <div key="startDate" style={{ fontSize: '1.0vw' }}>
                      <strong>Start Date: </strong>
                      {formatDate(state.event.dateStarted)}
                    </div>,
                    <div key="endDate" style={{ fontSize: '1.0vw' }}>
                      <strong>End Date: </strong>
                      {formatDate(state.event.dateEnded)}
                    </div>,
                    <div key="jobNumber" style={{ fontSize: '1.0vw' }}>
                      <strong>Job Number: </strong>
                      {state.event.logJobNumber}
                    </div>,
                  ]}
                />
                <div key="CostSummary">
                  <InfoTable
                    columns={[
                      { name: 'Type', align: 'left' },
                      { name: 'Total', align: 'left' },
                      { name: '', align: 'right' },
                    ]}
                    data={[
                      [
                        {
                          value: (
                            <div key="laborheader">
                              Total Hours Worked
                              <div key="LaborTypesContainer">
                                <Collapse
                                  key={'LaborCollapseTypes'}
                                  in={state.laborTotalsDropDownActive === true}
                                >
                                  {state.classCodes.map((code) => {
                                    const findAccount = code.id;
                                    if (state.laborTotals[findAccount]) {
                                      return (
                                        <div key={'laborValue' + findAccount}>
                                          {code.description}
                                        </div>
                                      );
                                    }
                                    return null;
                                  })}
                                </Collapse>
                              </div>
                            </div>
                          ),
                        },
                        {
                          value: (
                            <div key="laborTotalHeader">
                              {state.totalHoursWorked > 1
                                ? `${state.totalHoursWorked} hrs`
                                : state.totalHoursWorked == 0
                                  ? 'None'
                                  : `${state.totalHoursWorked} hr`}
                              <div key="LaborCollapseValueHeader">
                                <Collapse
                                  key={'LaborCollapseValues'}
                                  in={state.laborTotalsDropDownActive === true}
                                >
                                  {state.classCodes.map((code) => {
                                    const findAccount = code.id;
                                    if (state.laborTotals[findAccount]) {
                                      return (
                                        <div
                                          key={
                                            'laborTotal' +
                                            state.laborTotals[findAccount] +
                                            code.description
                                          }
                                        >
                                          {`${state.laborTotals[findAccount]} hour(s)`}
                                        </div>
                                      );
                                    }
                                    return null;
                                  })}
                                </Collapse>
                              </div>
                            </div>
                          ),
                        },
                        {
                          value: (
                            <div key="LaborTotalsButton">
                              <Button
                                key={'dropDownbuttonLabor'}
                                onClick={() => {
                                  dispatch({
                                    type: ACTIONS.SET_LABOR_TOTALS_DROPDOWN_ACTIVE,
                                    data: !state.laborTotalsDropDownActive,
                                  });
                                }}
                              >
                                {state.laborTotalsDropDownActive == true ? (
                                  <ExpandLess></ExpandLess>
                                ) : (
                                  <ExpandMore></ExpandMore>
                                )}
                              </Button>
                            </div>
                          ),
                        },
                      ],
                    ]}
                  />
                  {employeeHourTotals.length > 0 && (
                    <InfoTable
                      columns={[
                        { name: 'Type', align: 'left' },
                        { name: 'Total', align: 'left' },
                        { name: '', align: 'right' },
                      ]}
                      data={[
                        [
                          {
                            value: (
                              <div key="employeeHoursHeader">
                                Hours by Employee
                                <Collapse
                                  key="EmployeeHoursCollapseNames"
                                  in={state.employeeHoursDropDownActive === true}
                                >
                                  {employeeHourTotals.map((employee) => (
                                    <div key={'employeeHoursName' + employee.employeeId}>
                                      {employee.employeeName}
                                    </div>
                                  ))}
                                </Collapse>
                              </div>
                            ),
                          },
                          {
                            value: (
                              <div key="employeeHoursValues">
                                <Collapse
                                  key="EmployeeHoursCollapseValues"
                                  in={state.employeeHoursDropDownActive === true}
                                >
                                  {employeeHourTotals.map((employee) => (
                                    <div key={'employeeHoursValue' + employee.employeeId}>
                                      {`${roundNumber(employee.hours)} hour(s)`}
                                    </div>
                                  ))}
                                </Collapse>
                              </div>
                            ),
                          },
                          {
                            value: (
                              <div key="EmployeeHoursButton">
                                <Button
                                  key="dropDownbuttonEmployeeHours"
                                  onClick={() => {
                                    dispatch({
                                      type: ACTIONS.SET_EMPLOYEE_HOURS_DROPDOWN_ACTIVE,
                                      data: !state.employeeHoursDropDownActive,
                                    });
                                  }}
                                >
                                  {state.employeeHoursDropDownActive == true ? (
                                    <ExpandLess></ExpandLess>
                                  ) : (
                                    <ExpandMore></ExpandMore>
                                  )}
                                </Button>
                              </div>
                            ),
                          },
                        ],
                      ]}
                    />
                  )}
                  <PrintParagraph className="pb-1 pl-3" tag="h2">
                    Costs
                  </PrintParagraph>
                  <InfoTable
                    columns={[
                      { name: 'Type', align: 'left' },
                      { name: 'Cost', align: 'left' },
                      { name: '', align: 'right' },
                    ]}
                    data={[
                      [
                        {
                          value: (
                            <div key="CostCenterCollapseTypesContainer">
                              Transactions
                              <Collapse
                                key={'CostCenterCollapseTypes'}
                                in={state.costCenterDropDownActive === true}
                              >
                                {state.transactionAccounts.map((account) => {
                                  // Lodging/Travel (643003) is shown under Lodging, not here.
                                  if (account.id === LODGING_TRAVEL_COST_CENTER_ID) return null;
                                  const findAccount = `${account.id}-${account.description}`;
                                  if (state.costCenterTotals[findAccount]) {
                                    return (
                                      <div
                                        key={'transactionAccountValue' + findAccount + account.id}
                                      >
                                        {findAccount}
                                      </div>
                                    );
                                  }
                                  return null;
                                })}
                              </Collapse>
                            </div>
                          ),
                        },
                        {
                          value: (
                            <div key="CostCenterCollapseValueHeader">
                              {usd(totalTransactions)}
                              <Collapse
                                key={'CostCenterCollapseValues'}
                                in={state.costCenterDropDownActive === true}
                              >
                                {state.transactionAccounts.map((account) => {
                                  // Lodging/Travel (643003) is shown under Lodging, not here.
                                  if (account.id === LODGING_TRAVEL_COST_CENTER_ID) return null;
                                  const findAccount = `${account.id}-${account.description}`;
                                  if (state.costCenterTotals[findAccount]) {
                                    return (
                                      <div
                                        key={
                                          'transactionAccountValue' +
                                          state.costCenterTotals[findAccount] +
                                          findAccount
                                        }
                                      >
                                        {usd(state.costCenterTotals[findAccount])}
                                      </div>
                                    );
                                  }
                                  return null;
                                })}
                              </Collapse>
                            </div>
                          ),
                        },
                        {
                          value: (
                            <div key="transactionTotalsButton">
                              <Button
                                key={'dropDownbuttonTransactions'}
                                onClick={() => {
                                  dispatch({
                                    type: ACTIONS.SET_COST_CENTER_DROPDOWN_ACTIVE,
                                    data: !state.costCenterDropDownActive,
                                  });
                                }}
                              >
                                {state.costCenterDropDownActive == true ? (
                                  <ExpandLess></ExpandLess>
                                ) : (
                                  <ExpandMore></ExpandMore>
                                )}
                              </Button>
                            </div>
                          ),
                        },
                      ],
                      [{ value: 'Meals' }, { value: usd(totalMeals) }, { value: '' }],
                      [{ value: 'Lodging' }, { value: usd(totalLodging) }, { value: '' }],
                      [{ value: 'Spiffs' }, { value: usd(totalTasksBillable) }, { value: '' }],
                      [
                        { value: 'Reimbursements' },
                        { value: usd(totalReimbursements) },
                        { value: '' },
                      ],
                      [{ value: 'Trips Total' }, { value: usd(state.tripsTotal) }, { value: '' }],
                      [
                        { value: 'Total Miles' },
                        { value: `${roundNumber(totalMiles)} mi` },
                        { value: '' },
                      ],
                      [
                        {
                          value: <strong key="stronk1">TOTAL:</strong>,
                        },
                        {
                          value: (
                            <strong key="stronk2">
                              TOTAL:
                              {usd(
                                totalMeals +
                                  totalLodging +
                                  totalTransactions +
                                  totalTasksBillable +
                                  totalReimbursements +
                                  state.tripsTotal,
                              )}
                            </strong>
                          ),
                        },
                        { value: '' },
                      ],
                      ...(state.invoice
                        ? [
                            [{ value: '' }, { value: '' }, { value: '' }],
                            ...(state.invoice.discountcost &&
                            parseFloat(state.invoice.discountcost) > 0
                              ? [
                                  [
                                    { value: 'Discount' },
                                    { value: usd(parseFloat(state.invoice.discountcost)) },
                                    { value: '' },
                                  ],
                                ]
                              : []),
                            ...(state.event?.taxCost
                              ? [
                                  [
                                    { value: 'Sales Tax' },
                                    { value: usd(state.event.taxCost) },
                                    { value: '' },
                                  ],
                                ]
                              : []),
                            [
                              { value: <strong key="invoiceHeader">Invoice Total</strong> },
                              {
                                value: (
                                  <strong key="invoiceTotal">
                                    {usd(
                                      parseFloat(state.invoice.totalamountrow1 || '0') +
                                        parseFloat(state.invoice.totalamountrow2 || '0') +
                                        parseFloat(state.invoice.totalamountrow3 || '0') +
                                        parseFloat(state.invoice.totalamountrow4 || '0') +
                                        parseFloat(state.invoice.materialTotal || '0') -
                                        parseFloat(state.invoice.discountcost || '0') +
                                        (state.event?.taxCost || 0),
                                    )}
                                  </strong>
                                ),
                              },
                              { value: '' },
                            ],
                          ]
                        : []),
                      ...(state.payments.length > 0
                        ? [
                            [
                              {
                                value: (
                                  <strong key="paymentsHeader">Total Payments Received</strong>
                                ),
                              },
                              {
                                value: (
                                  <strong key="paymentsTotal">
                                    {usd(
                                      state.payments.reduce(
                                        (aggr, payment) => aggr + (payment.amountCollected || 0),
                                        0,
                                      ),
                                    )}
                                  </strong>
                                ),
                              },
                              { value: '' },
                            ],
                          ]
                        : []),
                      ...(state.invoice && state.payments.length > 0
                        ? [
                            [
                              { value: <strong key="balanceHeader">Outstanding Balance</strong> },
                              {
                                value: (
                                  <strong key="balance">
                                    {usd(
                                      parseFloat(state.invoice.totalamountrow1 || '0') +
                                        parseFloat(state.invoice.totalamountrow2 || '0') +
                                        parseFloat(state.invoice.totalamountrow3 || '0') +
                                        parseFloat(state.invoice.totalamountrow4 || '0') +
                                        parseFloat(state.invoice.materialTotal || '0') -
                                        parseFloat(state.invoice.discountcost || '0') +
                                        (state.event?.taxCost || 0) -
                                        state.payments.reduce(
                                          (aggr, payment) => aggr + (payment.amountCollected || 0),
                                          0,
                                        ),
                                    )}
                                  </strong>
                                ),
                              },
                              { value: '' },
                            ],
                          ]
                        : []),
                    ]}
                  />
                </div>
              </div>
            ),
          },

          {
            label: 'Invoice & Materials',
            content: state.invoice ? (
              <div className="p-4">
                <SectionBar title="Invoice Details">
                  <div className="mb-4">
                    <h3 className="mb-2 text-lg font-semibold">Services Performed</h3>
                    <InfoTable
                      columns={[
                        { name: 'Description', align: 'left' },
                        { name: 'Amount', align: 'right' },
                      ]}
                      data={[
                        ...(state.invoice.servicesperformedrow1
                          ? [
                              [
                                { value: state.invoice.servicesperformedrow1 },
                                { value: usd(parseFloat(state.invoice.totalamountrow1 || '0')) },
                              ],
                            ]
                          : []),
                        ...(state.invoice.servicesperformedrow2
                          ? [
                              [
                                { value: state.invoice.servicesperformedrow2 },
                                { value: usd(parseFloat(state.invoice.totalamountrow2 || '0')) },
                              ],
                            ]
                          : []),
                        ...(state.invoice.servicesperformedrow3
                          ? [
                              [
                                { value: state.invoice.servicesperformedrow3 },
                                { value: usd(parseFloat(state.invoice.totalamountrow3 || '0')) },
                              ],
                            ]
                          : []),
                        ...(state.invoice.servicesperformedrow4
                          ? [
                              [
                                { value: state.invoice.servicesperformedrow4 },
                                { value: usd(parseFloat(state.invoice.totalamountrow4 || '0')) },
                              ],
                            ]
                          : []),
                      ]}
                    />
                  </div>

                  {state.materials.length > 0 && (
                    <div className="mb-4">
                      <h3 className="mb-2 text-lg font-semibold">Materials</h3>
                      <InfoTable
                        columns={[
                          { name: 'Description', align: 'left' },
                          { name: 'Quantity', align: 'center' },
                          { name: 'Price', align: 'right' },
                          { name: 'Billable', align: 'center' },
                          { name: 'Total', align: 'right' },
                        ]}
                        data={state.materials.map((material) => {
                          const total = material.isBillable
                            ? material.quantity * material.quotedPrice
                            : 0;
                          return [
                            { value: material.description },
                            { value: material.quantity },
                            { value: usd(material.quotedPrice) },
                            { value: material.isBillable ? 'Yes' : 'No' },
                            { value: usd(total) },
                          ];
                        })}
                      />
                    </div>
                  )}

                  <div className="mt-4">
                    <InfoTable
                      columns={[
                        { name: '', align: 'left' },
                        { name: '', align: 'right' },
                      ]}
                      data={[
                        ...(state.invoice.discountcost && parseFloat(state.invoice.discountcost) > 0
                          ? [
                              [
                                { value: <strong>Discount</strong> },
                                {
                                  value: (
                                    <strong>{usd(parseFloat(state.invoice.discountcost))}</strong>
                                  ),
                                },
                              ],
                            ]
                          : []),
                        ...(state.event?.taxCost
                          ? [
                              [
                                { value: <strong>Sales Tax</strong> },
                                {
                                  value: <strong>{usd(state.event.taxCost)}</strong>,
                                },
                              ],
                            ]
                          : []),
                        [
                          { value: <strong>Grand Total</strong> },
                          {
                            value: (
                              <strong>
                                {usd(
                                  parseFloat(state.invoice.totalamountrow1 || '0') +
                                    parseFloat(state.invoice.totalamountrow2 || '0') +
                                    parseFloat(state.invoice.totalamountrow3 || '0') +
                                    parseFloat(state.invoice.totalamountrow4 || '0') +
                                    parseFloat(state.invoice.materialTotal || '0') -
                                    parseFloat(state.invoice.discountcost || '0') +
                                    (state.event?.taxCost || 0),
                                )}
                              </strong>
                            ),
                          },
                        ],
                      ]}
                    />
                  </div>
                </SectionBar>
              </div>
            ) : (
              <div className="p-4">
                <div className="text-center text-gray-500">No invoice found for this job.</div>
              </div>
            ),
          },

          {
            label: 'Payments',
            content:
              state.payments.length > 0 ? (
                <div className="p-4">
                  <SectionBar
                    title="Payments"
                    subtitle={`Total: ${usd(state.payments.reduce((aggr, payment) => aggr + (payment.amountCollected || 0), 0))}`}
                  >
                    <InfoTable
                      columns={[
                        { name: 'Service Rendered ID', align: 'left' },
                        { name: 'Amount Collected', align: 'right' },
                        { name: 'Type', align: 'left' },
                        { name: 'Collected', align: 'left' },
                      ]}
                      data={state.payments.map((payment) => [
                        { value: payment.servicesRenderedId || '-' },
                        { value: usd(payment.amountCollected || 0) },
                        { value: payment.type || '-' },
                        { value: payment.collected ? 'Yes' : 'No' },
                      ])}
                    />
                  </SectionBar>
                </div>
              ) : (
                <div className="p-4">
                  <div className="text-center text-gray-500">No payments found for this job.</div>
                </div>
              ),
          },

          {
            label: 'Transactions',
            content: state.transactionAccounts
              .filter((account) => state.transactions.some((t) => t.costCenterId === account.id))
              .map((account) => (
                <SectionBar
                  title={`${account.id}-${account.description}`}
                  subtitle={usd(
                    state.costCenterTotals[`${account.id}-${account.description}`] ?? 0,
                  )}
                  key={'header' + account.id}
                >
                  <InfoTable
                    key={account.id}
                    styles={{ width: '100%', padding: 10 }}
                    columns={[
                      {
                        name: 'Department',
                        align: 'left',
                      },
                      {
                        name: 'Owner',
                        align: 'left',
                      },
                      {
                        name: 'Cost Center / Vendor',
                        align: 'left',
                      },
                      {
                        name: 'Date',
                        align: 'left',
                        dir:
                          state.transactionSort.sortDir &&
                          state.transactionSort.sortBy == 'timestamp'
                            ? state.transactionSort.sortDir
                            : undefined,
                        onClick: () => {
                          const dir = state.transactionSort.sortDir == 'ASC' ? 'DESC' : 'ASC';
                          dispatch({
                            type: ACTIONS.SET_TRANSACTION_SORT,
                            data: {
                              sortBy: 'timestamp',
                              sortDir: dir,
                            },
                          });
                          dispatch({
                            type: ACTIONS.SET_TRANSACTIONS,
                            data: sortTransactions(state.transactions, dir, 'timestamp'),
                          });
                        },
                      },
                      {
                        name: 'Amount',
                        align: 'left',
                        dir:
                          state.transactionSort.sortDir && state.transactionSort.sortBy == 'amount'
                            ? state.transactionSort.sortDir
                            : undefined,
                        onClick: () => {
                          const dir = state.transactionSort.sortDir == 'ASC' ? 'DESC' : 'ASC';
                          dispatch({
                            type: ACTIONS.SET_TRANSACTION_SORT,
                            data: {
                              sortBy: 'amount',
                              sortDir: dir,
                            },
                          });
                          dispatch({
                            type: ACTIONS.SET_TRANSACTIONS,
                            data: sortTransactions(state.transactions, dir, 'amount'),
                          });
                        },
                      },
                      {
                        name: 'Status',
                        align: 'left',
                      },
                      {
                        name: 'Notes',
                        align: 'left',
                      },
                      {
                        name: 'Actions',
                        align: 'left',
                      },
                    ]}
                    data={state.transactions
                      .filter((transaction) => transaction.costCenterId === account.id)
                      .map((txn) => {
                        return [
                          {
                            value: txn.department ? (
                              <div key={txn.id + txn.description}>
                                {txn.department?.classification} - {txn.department?.description}
                              </div>
                            ) : (
                              '-'
                            ),
                          },

                          { value: txn.ownerName },

                          {
                            value: `${txn.costCenter?.description} - ${txn.vendor}`,
                          },
                          { value: formatDate(txn.timestamp) },
                          {
                            value: isDuplicateTransaction(txn) ? (
                              <span className="text-gray-400 line-through">{usd(txn.amount)}</span>
                            ) : (
                              usd(txn.amount)
                            ),
                          },
                          { value: getTransactionStatus(txn) },
                          {
                            value: <div style={{ fontSizeAdjust: '0.5' }}>{txn.notes}</div>,
                          },
                          {
                            value: (
                              <div className="flex gap-2">
                                <Tooltip content="View Photos" placement="top">
                                  <UiButton
                                    variant="outline"
                                    className="px-2 py-2"
                                    onClick={() =>
                                      dispatch({
                                        type: ACTIONS.SET_OPEN_GALLERY_ID,
                                        data: txn.id,
                                      })
                                    }
                                  >
                                    <CameraIcon />
                                    <span className="sr-only">View Photos</span>
                                  </UiButton>
                                </Tooltip>
                                <Dialog
                                  open={state.openGalleryId === txn.id}
                                  onOpenChange={() =>
                                    dispatch({
                                      type: ACTIONS.SET_OPEN_GALLERY_ID,
                                      data: undefined,
                                    })
                                  }
                                >
                                  <DialogContent className="h-screen w-screen max-w-[95vw] p-4">
                                    <div className="flex h-full w-full overflow-auto">
                                      <TransactionFilesGallery
                                        key="receiptPhotos"
                                        title="Transaction Photos"
                                        transactionId={txn.id}
                                        subTitle="View photos"
                                      />
                                    </div>
                                  </DialogContent>
                                </Dialog>
                                {hasReconcilePermission &&
                                  !txn.vendorCategory &&
                                  !isDuplicateTransaction(txn) && (
                                    <Tooltip content="Reconcile" placement="top">
                                      <UiButton
                                        variant="outline"
                                        className="px-2 py-2"
                                        onClick={() =>
                                          dispatch({
                                            type: ACTIONS.SET_OPEN_RECONCILE_ID,
                                            data: txn.id,
                                          })
                                        }
                                      >
                                        <BackpackIcon />
                                        <span className="sr-only">Reconcile</span>
                                      </UiButton>
                                    </Tooltip>
                                  )}
                              </div>
                            ),
                          },
                        ];
                      })}
                  />
                </SectionBar>
              )),
          },

          {
            label: 'PerDiems',
            content: state.users
              .filter((user) => state.perDiems.find((perDiem) => perDiem.userId === user.id))
              .map((user) => {
                {
                  return (
                    <SectionBar
                      key={user.id + user.dateCreated}
                      title={`PerDiems For ${user.firstname} ${user.lastname}`}
                    >
                      {sortedPerDiems
                        .filter((perdiem) => perdiem.userId == user.id)
                        .map((pd) => {
                          const rows = pd.rows;
                          const { meals: totalMeals, lodging: totalLodging } =
                            getPerDiemTotalsFromRows(rows);
                          return (
                            <div key={pd.id.toString() + 'div'}>
                              <div
                                style={{
                                  breakInside: 'avoid',
                                  display: 'inline-block',
                                  width: '100%',
                                }}
                                key="PerDiemContainer"
                              >
                                <InfoTable
                                  columns={[
                                    {
                                      name: 'Department',
                                      align: 'left',
                                    },
                                    {
                                      name: 'Owner',
                                      align: 'left',
                                    },
                                    {
                                      name: 'Submitted At',
                                      align: 'left',
                                      ...dateSortColumn('PerDiems'),
                                    },
                                    {
                                      name: 'Approved By',
                                      align: 'left',
                                    },
                                    {
                                      name: 'Approved At',
                                      align: 'left',
                                    },
                                    {
                                      name: 'Total Meals',
                                      align: 'left',
                                    },
                                    {
                                      name: 'Total Lodging',
                                      align: 'left',
                                    },
                                    {
                                      name: 'Status',
                                      align: 'left',
                                    },
                                    {
                                      name: 'Notes',
                                      align: 'left',
                                    },
                                  ]}
                                  data={[
                                    [
                                      {
                                        value: TimesheetDepartmentClientService.getDepartmentName(
                                          pd.department!,
                                        ),
                                      },
                                      { value: pd.ownerName },
                                      {
                                        value:
                                          pd.dateSubmitted != NULL_TIME
                                            ? formatDate(pd.dateSubmitted)
                                            : '-',
                                      },
                                      { value: pd.approvedByName || '-' },
                                      {
                                        value:
                                          pd.dateApproved != NULL_TIME
                                            ? formatDate(pd.dateApproved)
                                            : '-',
                                      },
                                      { value: usd(totalMeals) },
                                      {
                                        value: totalLodging != 0 ? usd(totalLodging) : '-',
                                      },
                                      { value: getPerDiemStatus(pd) },
                                      { value: pd.notes },
                                    ],
                                  ]}
                                />
                                <Button
                                  key={'dropDownbutton' + pd.id.toString()}
                                  onClick={() => {
                                    const tempDropDowns = state.dropDowns;
                                    const dropdown = tempDropDowns.findIndex(
                                      (dropdown) => dropdown.perDiemId === pd.id,
                                    );
                                    if (tempDropDowns[dropdown]) {
                                      // Legacy in-place toggle, unchanged. react-compiler flags it now that `state` comes from
                                      // useCostReport; unnamed disable because the root eslint config doesn't know that rule.
                                      /* eslint-disable */
                                      if (tempDropDowns[dropdown].active == 0)
                                        tempDropDowns[dropdown].active = 1;
                                      else tempDropDowns[dropdown].active = 0;
                                      /* eslint-enable */
                                    }
                                    dispatch({
                                      type: ACTIONS.SET_DROPDOWNS,
                                      data: tempDropDowns,
                                    });
                                  }}
                                >
                                  Details
                                  {state.dropDowns.find((dropdown) => dropdown.perDiemId === pd.id)!
                                    .active == 1 ? (
                                    <ExpandLess></ExpandLess>
                                  ) : (
                                    <ExpandMore></ExpandMore>
                                  )}
                                </Button>
                              </div>
                              <Collapse
                                key={pd.id.toString() + 'collapse'}
                                in={
                                  state.dropDowns.find((dropdown) => dropdown.perDiemId === pd.id)
                                    ?.active == 1
                                    ? true
                                    : false
                                }
                              >
                                <InfoTable
                                  key={pd.id.toString() + 'days'}
                                  columns={[
                                    {
                                      name: 'Date',
                                      align: 'left',
                                    },
                                    {
                                      name: 'Zip Code',
                                      align: 'left',
                                    },
                                    {
                                      name: 'Meals Only',
                                      align: 'left',
                                    },
                                    {
                                      name: 'Meals',
                                      align: 'left',
                                    },
                                    {
                                      name: 'Lodging',
                                      align: 'left',
                                    },
                                    {
                                      name: 'Notes',
                                      align: 'left',
                                    },
                                  ]}
                                  data={rows.map((pdr) => {
                                    return [
                                      {
                                        value: formatDate(pdr.dateString),
                                      },
                                      { value: pdr.zipCode },
                                      {
                                        value: pdr.mealsOnly ? 'Yes' : 'No',
                                      },
                                      {
                                        value: usd(pdr.mealsValue || 0),
                                      },
                                      {
                                        value:
                                          pdr.mealsOnly === false
                                            ? usd(pdr.lodgingValue || 0)
                                            : '-',
                                      },
                                      { value: pdr.notes },
                                    ];
                                  })}
                                />
                              </Collapse>
                            </div>
                          );
                        })}
                    </SectionBar>
                  );
                }
              }),
          },

          {
            label: 'Timesheets',
            content: (
              <>
                <KalosButton
                  onClick={() =>
                    dispatch({
                      type: ACTIONS.SET_SHOW_EMPLOYEE_SUMMARY,
                      data: !state.showEmployeeSummary,
                    })
                  }
                  label={
                    state.showEmployeeSummary ? 'Show Class Code View' : 'Show Employee Summary'
                  }
                />
                {state.showEmployeeSummary ? (
                  <EmployeeSummaryView
                    data={transformTimesheetsToDataList(state.timesheets)}
                    startDate={new Date()}
                  />
                ) : (
                  state.classCodes
                    .filter((code) => state.laborTotals[code.id] != undefined)
                    .map((code) => (
                      <SectionBar
                        title={`${code.id}-${code.description}`}
                        subtitle={`${state.laborTotals[code.id]} hour(s)`}
                        key={'header' + code.id}
                      >
                        {weekArray
                          .filter((week) => week.code === code.id)
                          .map((weekMapValue) => (
                            <div key={weekMapValue.weekStart}>
                              <PrintParagraph
                                tag="h2"
                                className="pl-[7px]"
                              >{`Week Of ${weekMapValue.weekStart}`}</PrintParagraph>
                              <InfoTable
                                columns={[
                                  {
                                    name: 'Employee',
                                    align: 'left',
                                  },
                                  {
                                    name: 'Week',
                                    align: 'left',
                                  },
                                  {
                                    name: 'Total Hours',
                                    align: 'left',
                                  },
                                ]}
                                data={state.timesheetWeeklySubtotals
                                  .filter(
                                    (week) =>
                                      week.classCodeId == code.id &&
                                      weekMapValue.weekStart == week.weekStart,
                                  )
                                  .map((week) => {
                                    return [
                                      {
                                        value: `${
                                          state.users.find((user) => user.id === week.employeeId)
                                            ?.firstname
                                        }-${
                                          state.users.find((user) => user.id === week.employeeId)
                                            ?.lastname
                                        }`,
                                      },
                                      {
                                        value: `${week.weekStart}-${week.weekEnd}`,
                                      },
                                      { value: `${week.hoursSubtotal} hour(s)` },
                                    ];
                                  })}
                              />
                            </div>
                          ))}
                        <div key="TimesheetDetails">
                          <Button
                            key={'dropDownbutton' + code.id.toString()}
                            onClick={() => {
                              const tempDropDowns = state.classCodeDropdowns;
                              const dropdown = tempDropDowns.findIndex(
                                (dropdown) => dropdown.classCodeId === code.id,
                              );
                              if (tempDropDowns[dropdown]) {
                                if (tempDropDowns[dropdown].active == 0)
                                  tempDropDowns[dropdown].active = 1;
                                else tempDropDowns[dropdown].active = 0;
                              }
                              dispatch({
                                type: ACTIONS.SET_CLASS_CODE_DROPDOWNS,
                                data: tempDropDowns,
                              });
                            }}
                          >
                            Details
                            {state.classCodeDropdowns.find(
                              (dropdown) => dropdown.classCodeId === code.id,
                            )!.active == 1 ? (
                              <ExpandLess></ExpandLess>
                            ) : (
                              <ExpandMore></ExpandMore>
                            )}
                          </Button>
                          <Collapse
                            key={code.id.toString() + 'collapse'}
                            in={
                              state.classCodeDropdowns.find(
                                (dropdown) => dropdown.classCodeId === code.id,
                              )?.active == 1
                                ? true
                                : false
                            }
                          >
                            <InfoTable
                              columns={[
                                {
                                  name: 'Technician',
                                  align: 'left',
                                },
                                {
                                  name: 'Department',
                                  align: 'left',
                                },
                                {
                                  name: 'Approved By',
                                  align: 'left',
                                },
                                {
                                  name: 'Time Started',
                                  align: 'left',
                                  ...dateSortColumn('Timesheets'),
                                },
                                {
                                  name: 'Time Finished',
                                  align: 'left',
                                },
                                {
                                  name: 'Brief Description',
                                  align: 'left',
                                },
                                {
                                  name: 'Hours Worked',
                                  align: 'left',
                                },
                                {
                                  name: 'Status',
                                  align: 'left',
                                },
                                {
                                  name: 'Notes',
                                  align: 'right',
                                },
                              ]}
                              data={sortedTimesheets
                                .filter((timesheet) => timesheet.classCodeId === code.id)
                                .map((tsl) => {
                                  return [
                                    {
                                      value: tsl.technicianUserName + ` (${tsl.technicianUserId})`,
                                    },
                                    { value: tsl.departmentName },
                                    {
                                      value: tsl.adminApprovalUserName,
                                    },
                                    {
                                      value: formatDate(tsl.timeStarted) || '-',
                                    },
                                    {
                                      value: formatDate(tsl.timeFinished) || '-',
                                    },
                                    { value: tsl.briefDescription },
                                    {
                                      value:
                                        tsl.hoursWorked != 0
                                          ? tsl.hoursWorked > 1
                                            ? `${tsl.hoursWorked} hrs`
                                            : `${tsl.hoursWorked} hr`
                                          : '-',
                                    },
                                    { value: getTimesheetStatus(tsl) },
                                    { value: tsl.notes },
                                  ];
                                })}
                            />
                          </Collapse>
                        </div>
                      </SectionBar>
                    ))
                )}
              </>
            ),
          },

          {
            label: 'Trips',
            content: state.users
              .filter((user) => state.trips.find((trip) => trip.userId === user.id))
              .map((user) => {
                return (
                  <SectionBar
                    key={user.id + 'Trips'}
                    title={`${user.firstname} ${user.lastname} Trips`}
                    subtitle={`Total Miles: ${roundNumber(
                      state.trips
                        .filter((el) => el.userId === user.id)
                        .reduce((acc, trip) => acc + trip.distanceInMiles, 0),
                    )} mi`}
                  >
                    {state.trips &&
                      sortedTrips
                        .filter((el) => el.userId == user.id)
                        .map((trip) => {
                          return (
                            <div
                              key={trip.id}
                              style={{
                                breakInside: 'avoid',
                                display: 'inline-block',
                                width: '100%',
                              }}
                            >
                              <InfoTable
                                columns={[
                                  {
                                    name: 'Date',
                                    align: 'left',
                                    ...dateSortColumn('Trips'),
                                  },
                                  {
                                    name: 'Origin Address',
                                    align: 'left',
                                  },
                                  {
                                    name: 'Destination Address',
                                    align: 'left',
                                  },
                                  {
                                    name: 'Distance (Miles)',
                                    align: 'left',
                                  },
                                  {
                                    name: 'Notes',
                                    align: 'left',
                                  },
                                  {
                                    name: 'Home Travel',
                                    align: 'right',
                                  },
                                  {
                                    name: `Cost (${usd(IRS_SUGGESTED_MILE_FACTOR)}/mi)`,
                                    align: 'right',
                                  },
                                  {
                                    name: 'Per Diem Row ID',
                                    align: 'right',
                                  },
                                  {
                                    name: 'Status',
                                    align: 'right',
                                  },
                                ]}
                                data={[
                                  [
                                    { value: formatDate(trip.date) },
                                    { value: trip.originAddress },
                                    { value: trip.destinationAddress },
                                    {
                                      value: trip.distanceInMiles.toFixed(2),
                                    },
                                    { value: trip.notes },
                                    { value: trip.homeTravel },
                                    {
                                      value: `${usd(
                                        Number(
                                          (
                                            trip.distanceInMiles * IRS_SUGGESTED_MILE_FACTOR
                                          ).toFixed(2),
                                        ),
                                      )}
                          ${''}`,
                                    },
                                    { value: trip.perDiemRowId },
                                    { value: getTripStatus(trip) },
                                  ],
                                ]}
                              />
                            </div>
                          );
                        })}
                  </SectionBar>
                );
              }),
          },
          {
            label: 'Spiffs',
            content: state.users
              .filter((user) => state.tasks.find((task) => task.externalId === user.id))
              .map((user) => {
                return (
                  <SectionBar
                    key={user.id + 'Spiffs'}
                    title={`${user.firstname} ${user.lastname} Spiffs`}
                    subtitle={`Total: ${usd(
                      state.tasks &&
                        state.tasks
                          .filter((task) => task.externalId === user.id && spiffCounts(task))
                          .reduce((acc, val) => acc + val.spiffAmount, 0),
                    )}`}
                  >
                    {state.tasks &&
                      sortedTasks
                        .filter((task) => task.externalId === user.id)
                        .map((task) => {
                          return (
                            <div
                              key={task.id}
                              style={{
                                breakInside: 'avoid',
                                display: 'inline-block',
                                width: '100%',
                              }}
                            >
                              <InfoTable
                                columns={[
                                  {
                                    name: 'Type',
                                    align: 'left',
                                  },
                                  {
                                    name: 'Date Performed',
                                    align: 'left',
                                    ...dateSortColumn('Spiffs'),
                                  },
                                  {
                                    name: 'Details',
                                    align: 'left',
                                  },
                                  {
                                    name: 'Amount',
                                    align: 'left',
                                  },
                                  {
                                    name: 'Status',
                                    align: 'left',
                                  },
                                  {
                                    name: 'Notes',
                                    align: 'right',
                                  },
                                ]}
                                data={[
                                  [
                                    { value: task.billableType },
                                    {
                                      value: formatDate(task.datePerformed),
                                    },
                                    {
                                      value: task.details,
                                    },
                                    { value: usd(task.spiffAmount) },
                                    { value: getSpiffStatus(task) },
                                    { value: task.notes },
                                  ],
                                ]}
                              />
                            </div>
                          );
                        })}
                  </SectionBar>
                );
              }),
          },
          {
            label: 'Reimbursements',
            content: state.users
              .filter((user) => state.reimbursements.find((r) => r.userId === user.id))
              .map((user) => {
                return (
                  <SectionBar
                    key={user.id + 'Reimbursements'}
                    title={`${user.firstname} ${user.lastname} Reimbursements`}
                    subtitle={`Total: ${usd(
                      state.reimbursements
                        .filter((r) => r.userId === user.id && reimbursementCounts(r))
                        .reduce((acc, r) => acc + r.amount, 0),
                    )}`}
                  >
                    <InfoTable
                      columns={[
                        {
                          name: 'Date',
                          align: 'left',
                          ...dateSortColumn('Reimbursements'),
                        },
                        {
                          name: 'Description',
                          align: 'left',
                        },
                        {
                          name: 'Reviewed By',
                          align: 'left',
                        },
                        {
                          name: 'Amount',
                          align: 'left',
                        },
                        {
                          name: 'Status',
                          align: 'right',
                        },
                      ]}
                      data={sortedReimbursements
                        .filter((r) => r.userId === user.id)
                        .map((r) => [
                          { value: formatDate(r.createdDate) },
                          { value: r.description },
                          { value: r.reviewerName || '-' },
                          { value: usd(r.amount) },
                          { value: getReimbursementStatus(r) },
                        ])}
                    />
                  </SectionBar>
                );
              }),
          },
        ]}
      ></Tabs>
    </SectionBar>
  ) : loader ? (
    loader
  ) : (
    <Loader className="Job-Cost-Loading" />
  );
};
